import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const RefundDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  invoiceId = "",
  amount = 0
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return; // Don't submit if no reason provided
    }

    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim());
      handleClose();
    } catch (error) {
      console.error(`[Refund Dialog] Error processing refund:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        {/* Centering trick */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="bg-white px-6 py-6">
            <div className="flex items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="ml-4 mt-0 text-left flex-1">
                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Refund Invoice
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    Are you sure you want to refund invoice <strong>{invoiceId}</strong>? 
                    {amount > 0 && ` This will process a refund of $${amount.toFixed(2)}.`}
                    <br />
                    <span className="text-red-600 font-medium">This action cannot be undone.</span>
                  </p>
                  
                  <div>
                    <label htmlFor="refund-reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Refund Reason <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="refund-reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6"
                      placeholder="Please provide a reason for this refund..."
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={!reason.trim() || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RefundDialog;